// Project Component Task Categories Management
import { db } from './firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';

// Default component task categories (service-oriented)
const defaultComponentCategories = [
  { name: 'upholstery', label: 'Upholstery' },
  { name: 'lighting', label: 'Lighting' },
  { name: 'furniture', label: 'Furniture' },
  { name: 'fabric', label: 'Fabric' },
  { name: 'design', label: 'Design' },
  { name: 'other', label: 'Other' }
];

// Get component categories from Firestore or return defaults
export const getComponentCategories = async () => {
  try {
    const categoriesRef = doc(db, 'settings', 'componentCategories');
    const categoriesDoc = await getDoc(categoriesRef);
    
    if (categoriesDoc.exists()) {
      const data = categoriesDoc.data();
      return data.categories || defaultComponentCategories;
    }
    
    // If no categories exist in Firestore, return defaults
    return defaultComponentCategories;
  } catch (error) {
    console.error('Error getting component categories:', error);
    return defaultComponentCategories;
  }
};

// Save component categories to Firestore
export const saveComponentCategories = async (categories) => {
  try {
    const categoriesRef = doc(db, 'settings', 'componentCategories');
    
    await setDoc(categoriesRef, {
      categories: categories,
      updatedAt: serverTimestamp()
    });
    
    return true;
  } catch (error) {
    console.error('Error saving component categories:', error);
    throw error;
  }
};

// Add a new component category
export const addComponentCategory = async (categoryName, categoryLabel) => {
  try {
    const currentCategories = await getComponentCategories();
    const normalizedName = categoryName.toLowerCase().trim().replace(/\s+/g, '_');
    
    // Check if category already exists
    if (currentCategories.some(cat => cat.name === normalizedName)) {
      throw new Error('Category already exists');
    }
    
    const updatedCategories = [...currentCategories, { 
      name: normalizedName, 
      label: categoryLabel || categoryName 
    }];
    await saveComponentCategories(updatedCategories);
    
    return updatedCategories;
  } catch (error) {
    console.error('Error adding component category:', error);
    throw error;
  }
};

// Remove a component category
export const removeComponentCategory = async (categoryName) => {
  try {
    const currentCategories = await getComponentCategories();
    const updatedCategories = currentCategories.filter(cat => cat.name !== categoryName);
    
    await saveComponentCategories(updatedCategories);
    
    return updatedCategories;
  } catch (error) {
    console.error('Error removing component category:', error);
    throw error;
  }
};

// Update a component category's label
export const updateComponentCategoryLabel = async (categoryName, newLabel) => {
  try {
    const currentCategories = await getComponentCategories();
    const updatedCategories = currentCategories.map(cat => 
      cat.name === categoryName ? { ...cat, label: newLabel } : cat
    );
    
    await saveComponentCategories(updatedCategories);
    
    return updatedCategories;
  } catch (error) {
    console.error('Error updating component category:', error);
    throw error;
  }
};

// Format category for display
export const formatComponentCategoryForDisplay = (category) => {
  return typeof category === 'object' ? category.label : category;
};